/**
 * AppPanel Types
 * 
 * Type definitions for the AppPanel component which provides
 * an integrated view of a Parse Server application including
 * preview, database, cloud code, deployments, and logs.
 */

/**
 * Available view modes for the AppPanel
 * Each mode displays a different aspect of the Parse Server app
 */
export type ViewMode = 'preview' | 'database' | 'cloud-code' | 'deployments' | 'logs';

/**
 * Configuration for a view mode option in the selector
 */
export interface ViewModeOption {
  /** Unique identifier for the view mode */
  id: ViewMode;
  /** Display label shown in the selector */
  label: string;
  /** Icon component or identifier */
  iconId: string;
}

/**
 * All available view mode options with their configurations
 */
export const VIEW_MODE_OPTIONS: ViewModeOption[] = [
  { id: 'preview',     label: 'Preview',      iconId: 'preview' },
  { id: 'database',    label: 'Database',     iconId: 'database' },
  { id: 'cloud-code',  label: 'Cloud Code',   iconId: 'cloud-code' },
  { id: 'deployments', label: 'Deployments',  iconId: 'deployments' },
  // TODO: Uncomment when Logs view is implemented
  // { id: 'logs',        label: 'Logs',         iconId: 'logs' },
];

/**
 * Represents a Parse class (collection) in the database
 */
export interface ParseClass {
  /** Name of the class (e.g., "_User", "Vehicle") */
  name: string;
  /** Number of rows/documents in the class */
  rowCount: number;
  /** List of field names in the class schema */
  fields: string[];
}

/**
 * Represents a single row/document in a Parse class
 */
export interface ClassRow {
  /** Parse object ID */
  objectId: string;
  /** Creation timestamp */
  createdAt?: string;
  /** Last update timestamp */
  updatedAt?: string;
  /** Dynamic fields from the class schema */
  [key: string]: unknown;
}

/**
 * Represents a Cloud Function defined in the Parse Server
 */
export interface CloudFunction {
  /** Name of the cloud function (from Parse.Cloud.define) */
  name: string;
  /** File path where the function is defined */
  filePath?: string;
  /** Optional description of what the function does */
  description?: string;
}

/**
 * All Parse Server trigger types
 * Includes class triggers, auth triggers, and LiveQuery triggers
 */
export type TriggerType = 
  // Class triggers
  | 'beforeSave' 
  | 'afterSave' 
  | 'beforeDelete' 
  | 'afterDelete' 
  | 'beforeFind'
  | 'afterFind'
  // Auth triggers (for _User class)
  | 'beforeLogin'
  | 'afterLogin'
  | 'beforeLogout'
  | 'afterLogout'
  // LiveQuery triggers
  | 'beforeSubscribe'
  | 'beforeConnect'
  | 'afterEvent';

/**
 * Represents a trigger attached to a Parse class
 */
export interface Trigger {
  /** Type of trigger event */
  type: TriggerType;
  /** Class name the trigger is attached to */
  className: string;
  /** File path where the trigger is defined */
  filePath?: string;
}

/**
 * Type of cloud code item
 */
export type CloudCodeType = 'function' | 'trigger' | 'job';

/**
 * Unified representation of any cloud code item (function, trigger, or job)
 * Used for the unified cloud code list view
 */
export interface CloudCodeItem {
  /** Unique identifier (name + type + target for uniqueness) */
  id: string;
  /** Name of the function/trigger/job */
  name: string;
  /** Type of cloud code */
  type: CloudCodeType;
  /** For triggers: the trigger type (beforeSave, afterSave, etc.) */
  triggerType?: TriggerType;
  /** For triggers: the target class name */
  targetClass?: string;
  /** File path where the code is defined */
  filePath: string;
  /** The actual code of this function/trigger */
  code: string;
  /** REST endpoint (only for functions) */
  endpoint?: string;
}

/**
 * Types of deployment operations
 */
export type DeploymentType = 'cloud-code' | 'public-files' | 'schema' | 'config' | 'app-created';

/**
 * Status of a deployment
 */
export type DeploymentStatus = 'success' | 'failed' | 'pending' | 'in-progress';

/**
 * Represents a deployment event in the app history
 */
export interface Deployment {
  /** Unique identifier */
  id: string;
  /** Sequential deployment number */
  number: number;
  /** When the deployment occurred */
  timestamp: Date;
  /** Current status of the deployment */
  status: DeploymentStatus;
  /** Type of deployment operation */
  type: DeploymentType;
  /** Human-readable description of what was deployed */
  description: string;
  /** Optional details about files or changes */
  details?: string[];
}

/**
 * Log severity levels
 */
export type LogLevel = 'info' | 'warn' | 'error' | 'debug';

/**
 * Types of log entries
 */
export type LogType = 'api' | 'cloud-function' | 'trigger' | 'system' | 'auth';

/**
 * Represents a single log entry
 */
export interface LogEntry {
  /** Unique identifier */
  id: string;
  /** When the log was created */
  timestamp: Date;
  /** Severity level */
  level: LogLevel;
  /** Category of the log */
  type: LogType;
  /** Log message content */
  message: string;
  /** Optional additional data */
  metadata?: Record<string, unknown>;
}

/**
 * Filter options for the logs view
 */
export interface LogFilters {
  /** Filter by log type (null = all) */
  type: LogType | null;
  /** Filter by severity level (null = all) */
  level: LogLevel | null;
  /** Time range filter */
  timeRange: '1h' | '6h' | '24h' | '7d' | '30d';
  /** Whether to auto-refresh logs */
  isLive: boolean;
}

/**
 * Represents a page/file in the public hosting folder
 */
export interface PublicFile {
  /** File name */
  name: string;
  /** File path relative to public folder */
  path: string;
  /** File type (html, css, js, etc.) */
  type: string;
  /** File size in bytes */
  size?: number;
}

// ============================================================================
// Cloud Code Tree Types (from Back4App /parse-app/{appId}/cloud endpoint)
// ============================================================================

/**
 * Node in the cloud code tree (jsTree format from Back4App)
 */
export interface CloudTreeNode {
  /** Name of the file or folder */
  text: string;
  /** Type: 'folder' or undefined (file) */
  type?: 'folder';
  /** Visual state for jsTree (can be ignored) */
  state?: { opened?: boolean };
  /** Children nodes (only for folders) */
  children?: CloudTreeNode[];
  /** File data */
  data?: {
    /** Content in base64: "data:plain/text;base64,..." */
    code?: string;
  };
}

/**
 * Response from /parse-app/{appId}/cloud endpoint
 */
export interface CloudTreeResponse {
  tree: CloudTreeNode[];
}

/**
 * Extracted file from tree (for UI consumption)
 */
export interface ExtractedFile {
  /** File name */
  name: string;
  /** Full path from root (e.g., "public/index.html") */
  path: string;
  /** File type based on extension */
  type: 'html' | 'js' | 'css' | 'json' | 'other';
  /** Decoded content (optional, loaded on demand) */
  content?: string;
}

// ============================================================================
// Parse Server Schema Types (from /schemas endpoint)
// ============================================================================

/**
 * Field definition in a Parse schema
 */
export interface ParseFieldDefinition {
  /** Field type (String, Number, Pointer, etc.) */
  type: string;
  /** Target class for Pointer/Relation types */
  targetClass?: string;
  /** Whether the field is required */
  required?: boolean;
}

/**
 * Schema of a Parse class (from /schemas endpoint)
 */
export interface ParseSchema {
  /** Class name (e.g., "_User", "Vehicle") */
  className: string;
  /** Field definitions */
  fields: Record<string, ParseFieldDefinition>;
  /** Class level permissions */
  classLevelPermissions?: Record<string, unknown>;
  /** Indexes defined on the class */
  indexes?: Record<string, unknown>;
}

/**
 * Response from /schemas endpoint
 */
export interface ParseSchemasResponse {
  results: ParseSchema[];
}

/**
 * Row of data from a Parse class
 */
export interface ParseRow {
  /** Parse object ID */
  objectId: string;
  /** Creation timestamp */
  createdAt: string;
  /** Last update timestamp */
  updatedAt: string;
  /** Dynamic fields */
  [key: string]: unknown;
}

/**
 * Response from /classes/{className} endpoint
 */
export interface ParseClassDataResponse {
  results: ParseRow[];
  count?: number;
}

// ============================================================================
// Parse Field Type System (for Cell Renderers)
// ============================================================================

/**
 * All Parse field types supported by the database browser
 */
export type ParseFieldType = 
  | 'String'
  | 'Number'
  | 'Boolean'
  | 'Date'
  | 'Pointer'
  | 'Relation'
  | 'Array'
  | 'Object'
  | 'File'
  | 'GeoPoint'
  | 'Polygon'
  | 'ACL'
  | 'Bytes';

/**
 * Parse Pointer value structure
 */
export interface ParsePointer {
  __type: 'Pointer';
  className: string;
  objectId: string;
}

/**
 * Parse File value structure
 */
export interface ParseFile {
  __type: 'File';
  name: string;
  url: string;
}

/**
 * Parse GeoPoint value structure
 */
export interface ParseGeoPoint {
  __type: 'GeoPoint';
  latitude: number;
  longitude: number;
}

/**
 * Parse Date value structure (from REST API)
 */
export interface ParseDate {
  __type: 'Date';
  iso: string;
}

/**
 * Parse Relation value structure
 */
export interface ParseRelation {
  __type: 'Relation';
  className: string;
}

/**
 * Parse ACL value structure
 * Keys can be "*" for public, "role:RoleName", or userId
 */
export interface ParseACL {
  [key: string]: {
    read?: boolean;
    write?: boolean;
  };
}

/**
 * Parse Bytes value structure
 */
export interface ParseBytes {
  __type: 'Bytes';
  base64: string;
}

/**
 * Parse Polygon value structure
 */
export interface ParsePolygon {
  __type: 'Polygon';
  coordinates: [number, number][];
}

/**
 * Props passed to all cell renderer components
 */
export interface CellRendererProps<T = unknown> {
  /** The cell value */
  value: T;
  /** Field name */
  field: string;
  /** Parse field type */
  fieldType: ParseFieldType;
  /** Target class for Pointer/Relation types */
  targetClass?: string;
  /** Full row data */
  row: ParseRow;
  /** Callback when a Pointer is clicked */
  onPointerClick?: (pointer: ParsePointer) => void;
  /** Callback when a Relation is clicked */
  onRelationClick?: (className: string, objectId: string, field: string) => void;
  /** Dashboard URL for external links */
  dashboardUrl?: string;
  /** Callback to expand cell in modal */
  onExpand?: () => void;
}

// =============================================================================
// DEPLOYMENTS / RELEASES TYPES
// =============================================================================

/**
 * A release/deployment from the CLI API
 */
export interface Release {
  /** MongoDB ObjectId */
  _id: string;
  /** Sequential release number */
  releaseId: number;
  /** Version name (e.g., "v1", "v2") */
  releaseName: string;
  /** Description of the deployment */
  description: string;
  /** Timestamp when deployed */
  deployedAt: string;
  /** Timestamp when created */
  _created_at: string;
}

/**
 * File node type indicator from API
 * - folder: directory
 * - file: unchanged file
 * - green-file: added file
 * - yellow-file: modified file
 * - red-file: deleted file
 */
export type FileNodeType = 'folder' | 'file' | 'green-file' | 'yellow-file' | 'red-file';

/**
 * Semantic change type for display
 */
export type FileChangeType = 'added' | 'modified' | 'deleted' | 'unchanged';

/**
 * A node in the file changes tree
 */
export interface FileChangeNode {
  /** File or folder name */
  text: string;
  /** Node type (folder or file with color) */
  type: FileNodeType;
  /** Folder state (opened/closed) */
  state?: { opened: boolean };
  /** Child nodes (for folders) */
  children?: FileChangeNode[];
  /** File metadata (for files only) */
  data?: {
    code?: string;
    folder: string;
    filename: string;
    checksum: string;
    version: string;
  };
}

/**
 * Response from GET /cli/:id/releases
 */
export interface ReleasesListResponse {
  /** List of releases */
  data: Release[];
  /** Currently active deployment */
  currentDeployment: Release;
  /** Pagination info */
  pagination: {
    limit: number;
    hasMore: boolean;
    nextCursor: string | null;
    prevCursor: string | null;
    totalReturned: number;
    sort: 'asc' | 'desc';
  };
}

/**
 * Response from GET /cli/:id/releases/:releaseId
 */
export interface ReleaseDetailResponse {
  /** File changes tree */
  changes: {
    tree: FileChangeNode[];
  };
  /** Current release info */
  currentRelease: {
    releaseId: number;
    description: string;
    deployedAt: string;
    _id: string;
  };
  /** Whether rollback is available */
  isRollbackAvailable: boolean;
  /** Success flag */
  success: boolean;
}

/**
 * Response from POST /cli/:id/releases/:releaseId/file
 */
export interface FileContentResponse {
  /** S3 file URL */
  fileUrl: string;
  /** File content encoded in base64 */
  base64: string;
}

